import API, { get } from '/b2b/common/helpers/API';
import { API_NAME, CM_LOCALE_URL } from '#config/runtimeconfig';
import { CONSENT_EXPORT_TYPES, LATEST_GVL_VERSION } from './constants';
import {
    allSettled,
    assignResults,
    fetchJSON,
    formatFilterParams,
    formatPagingParams,
    handleError,
    makeResultsFormatter,
} from '../common/services/helpers';
import { assignCustomer, formatConsentAuditEventResults } from '../common/services/format';
import {
    formatConfig,
    formatConfigResults,
    formatConfigRevisionsResults,
    formatConsentDataResults,
    formatConsentReports,
    formatRuleResults,
    formatScanUrl,
    formatScanUrlResults,
    formatSitemapURL,
    formatSitemapURLResults,
    formatTattleResults,
} from './format';

import castArray from 'lodash/castArray';
import { createConsentConfigPayloadFromState } from './utils';
import noop from 'lodash/noop';
import pickBy from 'lodash/pickBy';

const languagesToLoad = [];
const languagesLoaded = {};
const loadLanguage = (language, onLoad) =>
    language &&
    fetchJSON(`${CM_LOCALE_URL}${language}.json`)
        .then(json => {
            languagesLoaded[language] = json;
            onLoad(language, json);
        })
        .catch(noop)
        .then(() => loadLanguage(languagesToLoad.shift(), onLoad));
export const preloadLanguages = (arrayOfLocaleCodes, onLoad) => {
    languagesToLoad.splice(
        0,
        languagesToLoad.length,
        ...arrayOfLocaleCodes.filter(language =>
            languagesLoaded[language]
                ? !!(onLoad(language, languagesLoaded[language]) && false)
                : true
        )
    );
    loadLanguage(languagesToLoad.shift(), onLoad);
    return function cancel() {
        languagesToLoad.splice(0, languagesToLoad.length);
    };
};

export const fetchConfigs = params => {
    const { customerId, filters } = params;
    const {
        publishStatus,
        mode,
        organizations: organizationsFilter,
        classificationStatus,
        tattling,
        unpublishedChanges,
        ucLinkable,
    } = filters || {};
    const organizations = organizationsFilter?.length
        ? castArray(organizationsFilter).map(o => (typeof o === 'string' ? o : o.value))
        : undefined;
    const queryParams = pickBy(
        {
            publishStatus,
            mode,
            organizations,
            classificationStatus,
            tattling,
            unpublishedChanges,
            ucLinkable,
        },
        value => !!value && value.toString().length
    );
    const options = {
        queryStringParameters: {
            ...formatPagingParams(params),
            ...queryParams,
        },
    };
    return API.get(API_NAME, `/consentmanager/configs`, options)
        .catch(handleError)
        .then(formatConfigResults)
        .then(assignResults(assignCustomer(customerId)));
};

export const fetchConfigById = ccId => {
    const options = {};
    return API.get(API_NAME, `/consentmanager/config/${encodeURIComponent(ccId)}`, options)
        .catch(handleError)
        .then(formatConfig);
};

// The customer comes from the session, not the path. Don't switch this to the
// CM CDN copy of the file; CloudFront serves it stale for ~24h.
export const fetchCustomFlavorMapping = () =>
    API.get(API_NAME, '/consentmanager/custom-flavor-mapping', {}).catch(handleError);

export const createConfig = state => {
    const body = createConsentConfigPayloadFromState(state);
    const options = {
        body,
    };
    return API.post(API_NAME, `/consentmanager/config`, options)
        .catch(handleError)
        .then(responseOrId =>
            typeof responseOrId === 'object'
                ? { ...state, ...responseOrId }
                : { id: responseOrId, ...state }
        )
        .then(formatConfig);
};

export const copyConfigById = ({ ccId, crossDomain, domains, name }) => {
    const options = {
        body: {
            crossDomain,
            domains,
            name,
        },
    };

    return API.post(
        API_NAME,
        `/consentmanager/config/${encodeURIComponent(ccId)}/duplicate`,
        options
    )
        .catch(handleError)
        .then(formatConfig);
};

export const updateConfigById = (ccId, updated, state) => {
    const body = createConsentConfigPayloadFromState(state);

    const options = {
        body: {
            ...body,
            updated,
        },
    };

    return API.patch(API_NAME, `/consentmanager/config/${encodeURIComponent(ccId)}`, options)
        .catch(handleError)
        .then(formatConfig);
};

export const deleteConfig = ccId => {
    const options = {
        responseType: 'text',
    };
    return API.del(API_NAME, `/consentmanager/config/${encodeURIComponent(ccId)}`, options)
        .then(response => {
            try {
                // Check if the response is JSON
                return JSON.parse(response);
            } catch (error) {
                return { status: 'success', code: 200, response };
            }
        })
        .catch(handleError)
        .then(response => {
            const { status, response: resp } = response;
            if (status === 'success') {
                return ccId;
            }
            return resp || response;
        });
};

export const deleteMultipleConfigs = configs => {
    return allSettled(configs.map(ccId => deleteConfig(ccId)));
};

export const publishConsentConfigById = ({
    ccId,
    description,
    keepUnclassifiedTattles,
    publish = true,
}) => {
    const options = {
        body: {
            description,
            keepUnclassifiedTattles,
            publish,
        },
    };
    return API.post(
        API_NAME,
        `/consentmanager/config/${encodeURIComponent(ccId)}/publish`,
        options
    ).catch(handleError);
};

export const deleteTattles = ccId => {
    return API.del(API_NAME, `/consentmanager/config/${ccId}/tattles`).catch(handleError);
};

export const fetchRevisionsByConfigId = params => {
    const { ccId } = params;
    const queryStringParameters = {
        ...formatPagingParams(params),
    };
    const options = {
        queryStringParameters,
    };
    return API.get(API_NAME, `/consentmanager/config/${ccId}/revisions`, options)
        .catch(handleError)
        .then(formatConfigRevisionsResults);
};

export const rollbackConsentVersion = params => {
    const { ccId, deployRevision, keepUnclassifiedTattles } = params;
    const options = {
        body: {
            keepUnclassifiedTattles,
            deployRevision,
        },
    };
    return API.post(
        API_NAME,
        `/consentmanager/config/${encodeURIComponent(ccId)}/publish`,
        options
    ).catch(handleError);
};

export const fetchConsentsByConfigId = params => {
    const { ccId, filters, nextToken } = params;

    let {
        consentedCategories,
        consentedDomains,
        consentedPlatforms,
        country,
        end,
        extUsrData,
        language,
        region,
        start,
        userConsentId,
        userIp,
    } = filters;

    if (start > end) {
        end = start;
    }
    const queryParams = pickBy(
        {
            userIp,
            start,
            end,
            consentedDomains,
            consentedPlatforms,
            userConsentId,
            consentedCategories,
            extUsrData,
            country,
            language,
            region,
        },
        value => !!value && value.toString().length
    );
    if (queryParams.consentedDomains) {
        queryParams.consentedDomains = castArray(queryParams.consentedDomains).join(',');
    }
    if (queryParams.consentedPlatforms) {
        queryParams.consentedPlatforms = castArray(queryParams.consentedPlatforms).join(',');
    }
    if (queryParams.consentedCategories) {
        queryParams.consentedCategories = castArray(queryParams.consentedCategories).join(',');
    }
    if (queryParams.language) {
        queryParams.language = castArray(queryParams.language).join(',');
    }
    if (queryParams.region) {
        queryParams.region = castArray(queryParams.region).join(',');
    }
    if (nextToken) {
        queryParams.nextToken = nextToken;
    }

    const options = {
        queryStringParameters: {
            ...formatPagingParams(params),
            ...queryParams,
        },
    };
    return API.get(API_NAME, `/consentmanager/config/${encodeURIComponent(ccId)}/consents`, options)
        .catch(handleError)
        .then(formatConsentDataResults);
};

export const fetchScanUrlsByConfigId = params => {
    const { ccId } = params;
    const queryStringParameters = {
        ...formatPagingParams(params),
        ...formatFilterParams(params),
    };
    const options = {
        queryStringParameters,
    };
    return API.get(API_NAME, `/scan/config/${ccId}/urls`, options)
        .catch(handleError)
        .then(formatScanUrlResults);
};

export const addScanUrl = params => {
    const { ccId, payload } = params;
    const options = {
        body: payload,
    };
    return API.post(API_NAME, `/scan/config/${ccId}/url`, options)
        .catch(handleError)
        .then(response => {
            if (!response) {
                return payload;
            }
            return response;
        })
        .then(formatScanUrl);
};

export const deleteScanUrl = params => {
    const { ccId, url } = params;
    const options = {
        body: {
            url,
        },
    };
    return API.del(API_NAME, `/scan/config/${ccId}/url`, options).catch(handleError);
};

export const deleteMultipleScanUrls = ({ ccId, urls }) => {
    return allSettled(urls.map(url => deleteScanUrl({ ccId, url }))).then(results => {
        const failures = results.filter(({ state }) => state === 'rejected');

        if (failures.length > 0) {
            throw failures[0].reason;
        }
    });
};

export const fetchIABVendorsByConfigId = params => {
    const { ccId, filters } = params;
    const queryStringParameters = {
        ...formatPagingParams(params),
        ...(filters || {}),
    };
    const options = {
        queryStringParameters,
    };
    return API.get(API_NAME, `/consentmanager/config/${ccId}/iab/vendors`, options)
        .catch(handleError)
        .then(makeResultsFormatter());
};

export const addIABVendorToConfig = params => {
    const { ccId, vendorId } = params;
    const options = {
        body: {
            vendorId,
        },
    };
    return API.post(API_NAME, `/consentmanager/config/${ccId}/iab/vendor`, options).catch(
        handleError
    );
};

export const deleteIABVendorFromConfig = params => {
    const { ccId, vendorId } = params;
    const options = {
        body: {
            vendorId,
        },
    };
    return API.del(API_NAME, `/consentmanager/config/${ccId}/iab/vendor`, options).catch(
        handleError
    );
};

export const deleteMultipleIABVendorsFromConfig = ({ ccId, vendorIds }) => {
    return allSettled(vendorIds.map(vendorId => deleteIABVendorFromConfig({ ccId, vendorId })));
};

export const fetchSitemaps = params => {
    const { ccId } = params;
    const queryStringParameters = {
        ...formatPagingParams(params),
        ...formatFilterParams(params),
    };
    const options = {
        queryStringParameters,
    };
    return API.get(API_NAME, `/scan/config/${ccId}/sitemaps`, options)
        .catch(handleError)
        .then(formatSitemapURLResults);
};

export const addSitemap = params => {
    const { ccId, payload } = params;
    const options = {
        body: payload,
    };
    return API.post(API_NAME, `/scan/config/${ccId}/sitemap`, options)
        .catch(handleError)
        .then(response => {
            if (!response) {
                return payload;
            }
            return response;
        })
        .then(formatSitemapURL);
};

export const deleteSitemapById = params => {
    const { ccId, url } = params;
    const options = {
        body: {
            url,
        },
    };
    return API.del(API_NAME, `/scan/config/${ccId}/sitemap`, options).catch(handleError);
};

export const deleteMultipleSitemapsById = ({ ccId, urls }) => {
    return allSettled(urls.map(url => deleteSitemapById({ ccId, url }))).then(results => {
        const failures = results.filter(({ state }) => state === 'rejected');

        if (failures.length > 0) {
            throw failures[0].reason;
        }
    });
};

export const updateSitemapById = (ccId, body) => {
    return API.patch(API_NAME, `/scan/config/${ccId}/sitemap`, { body })
        .catch(handleError)
        .then(response => {
            if (!response) {
                return body;
            }
            return response;
        });
};

export const updateScanUrlById = (ccId, body) => {
    return API.patch(API_NAME, `/scan/config/${ccId}/url`, { body })
        .catch(handleError)
        .then(response => {
            if (!response) {
                return body;
            }
            return response;
        });
};

const fetchTattles = params => {
    const { ccId, storeType = 'cookie' } = params;

    const queryStringParameters = {
        storeType,
        ...formatPagingParams(params),
        ...formatFilterParams(params),
    };

    const options = {
        queryStringParameters,
    };

    return API.get(
        API_NAME,
        `/consentmanager/config/${encodeURIComponent(ccId)}/categorized-tattles`,
        options
    )
        .catch(handleError)
        .catch(error => {
            // The most common reason for request failure is query string filters and sort, so lets remove them
            const url = document.location.href.split('?')[0];
            if (url !== document.location.href) {
                delete queryStringParameters.sortDir;
                delete queryStringParameters.sortField;
                return API.get(
                    API_NAME,
                    `/consentmanager/config/${encodeURIComponent(ccId)}/categorized-tattles`,
                    options
                );
            }
            throw error;
        });
};

const fetchRules = params => {
    const { ccId, storeType = 'cookie' } = params;

    const queryStringParameters = {
        storeType,
        ...formatPagingParams(params),
        ...formatFilterParams(params),
    };

    const options = {
        queryStringParameters,
    };

    return API.get(API_NAME, `/consentmanager/config/${encodeURIComponent(ccId)}/rules`, options)
        .catch(handleError)
        .catch(error => {
            // The most common reason for request failure is query string filters and sort, so lets remove them
            const url = document.location.href.split('?')[0];
            if (url !== document.location.href) {
                const newQueryStringParameters = {
                    storeType,
                    ...formatPagingParams(params),
                };
                delete newQueryStringParameters.sortDir;
                delete newQueryStringParameters.sortField;
                return API.get(
                    API_NAME,
                    `/consentmanager/config/${encodeURIComponent(ccId)}/rules`,
                    { queryStringParameters: newQueryStringParameters }
                );
            }
            throw error;
        });
};

const makeFetchOneThousand =
    (fetchPage, formatter) =>
    async ({ ccId, storeType = 'cookie', filters, ...otherParams }) => {
        const response = await fetchPage({
            ccId,
            storeType,
            page: 1,
            offset: 0,
            pageSize: 1000,
            limit: 1000,
            filters,
            ...otherParams,
        });

        const { results, hasNextPage } = formatter(response);

        return { results, hasNextPage };
    };

export const fetchAllRules = makeFetchOneThousand(fetchRules, formatRuleResults);

export const fetchAllTattles = makeFetchOneThousand(fetchTattles, formatTattleResults);

const RULE_ERROR_MESSAGES = [
    ["Invalid 'vendorId'", 'consent.errors.invalidVendor'],
    ["Provided 'rule' value", 'consent.errors.ruleAlreadyExists'],
    ['Invalid regular expression', 'consent.errors.invalidRuleRegex'],
    ['Catastrophic regular expression', 'consent.errors.ruleTooComplex'],
];

const handleRuleError = error => {
    const [, message] =
        RULE_ERROR_MESSAGES.find(
            ([prefix]) => typeof error?.message === 'string' && error.message.startsWith(prefix)
        ) || [];
    return handleError(message ? { ...error, response: { data: { message } } } : error);
};

export const publishMultipleRulesById = ({ ccId, rules: body = [] }) => {
    const options = {
        body,
    };

    return API.post(API_NAME, `/consentmanager/config/${encodeURIComponent(ccId)}/rules`, options)
        .catch(handleRuleError)
        .then(() => ({
            response: {},
        }));
};

export const updateMultipleRulesById = ({ ccId, body = [] }) => {
    const options = {
        body,
    };

    return (
        API.patch(API_NAME, `/consentmanager/config/${encodeURIComponent(ccId)}/rules`, options)
            .catch(handleRuleError)
            // Sometimes response will not contain the updated values we passed, so lets assume they were a success
            .then(response =>
                response?.results
                    ? response
                    : {
                          results: body,
                      }
            )
            .then(formatRuleResults)
    );
};

export const deleteMultipleRulesById = ({ ccId, ids, ruleIds = ids }) => {
    const options = {
        body: {
            ruleIds,
        },
    };

    return API.del(API_NAME, `/consentmanager/config/${encodeURIComponent(ccId)}/rules`, options)
        .catch(handleError)
        .then(() => ({
            response: {},
        }));
};

const iabVendorJsonCache = {};

export const fetchAllIabVendors = (params = {}) => {
    const { iabVersion = 2, territory = 'eu' } = params;
    return new Promise((resolve, reject) => {
        if (!iabVendorJsonCache[iabVersion]) {
            const url = new URL(
                `/assets/json/vendor-list.json?territory=${territory}&version=${iabVersion}`,
                `${document.location.protocol}//${document.location.host}`
            );
            const options = {
                method: 'GET',
                mode: 'cors',
                redirect: 'follow',
                referrer: 'no-referrer',
            };
            resolve(
                fetchJSON(url, options)
                    .catch(reject)
                    .then(json => {
                        iabVendorJsonCache[iabVersion] = json;
                        iabVendorJsonCache[iabVersion].lastUpdated =
                            json?.lastUpdated || new Date().toUTCString();
                        return json;
                    })
            );
        } else {
            resolve(iabVendorJsonCache[iabVersion]);
        }
    }).then(json => {
        return {
            gvlSpecificationVersion: json?.gvlSpecificationVersion || LATEST_GVL_VERSION,
            lastUpdated: json?.lastUpdated || new Date().toUTCString(),
            vendorListVersion: json?.vendorListVersion || 0,
            vendors: json?.vendors ? Object.values(json.vendors) : undefined,
        };
    });
};

export const fetchDisclosuresById = params => {
    const { customerId, configId } = params;

    return API.get(
        API_NAME,
        `/customer/${encodeURIComponent(customerId)}/config/${encodeURIComponent(
            configId
        )}/disclosures`
    ).catch(handleError);
};

export const fetchConsentReportsDashboard = (
    /** @type {import('../common/services/helpers').PagedParams & { filters: CmpReportsQueryParams }} */ params
) => {
    const queryStringParameters = {
        ...formatPagingParams(params),
        ...formatFilterParams(params, true),
    };
    const options = {
        queryStringParameters,
    };
    return API.get(API_NAME, '/consentmanager/reports/dashboard', options)
        .catch(handleError)
        .then(formatConsentReports);
};

export const fetchCMPAuditEvents = (params = {}) => {
    const options = {
        queryStringParameters: {
            ...formatFilterParams(params),
            ...formatPagingParams(params, true),
        },
    };

    return API.get(API_NAME, '/cmp/audit-log', options)
        .catch(handleError)
        .then(formatConsentAuditEventResults);
};

export const fetchUnpublishedChangesCount = ccId => {
    const options = {};
    return API.get(
        API_NAME,
        `/consentmanager/config/${encodeURIComponent(ccId)}/unpublished-changes-count`,
        options
    ).catch(handleError);
};

export const fetchRuleStats = (configId, storeType, storeKey) => {
    const { cancel, response } = get({
        apiName: API_NAME,
        path: `/consentmanager/config/${encodeURIComponent(configId)}/${encodeURIComponent(storeType)}/${encodeURIComponent(storeKey)}/stats`,
    });

    return {
        cancel,
        response: response.catch(handleError).then(({ body }) => body.json()),
    };
};

export const createBulkConfigExport = params => {
    const body = {
        type: CONSENT_EXPORT_TYPES.CMP_CONSENTS,
        params,
    };

    // The route for now is bulk config, this is to enforce RBAC on the export functionality.
    return API.post(API_NAME, '/export/bulk-config', { body }).catch(handleError);
};

export const createConsentCountsExport = params => {
    const body = {
        type: CONSENT_EXPORT_TYPES.CMP_CONSENT_COUNTS,
        params,
    };

    return API.post(API_NAME, '/export/consents-aggregate', { body }).catch(handleError);
};
